import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const CHAPTERS = [
  {
    nr: "01",
    title: "Stilte",
    text: "Alles begint in stilte. Niet als afwezigheid van geluid, maar als de grond waarop alles verschijnt — de toon, het woord, de adem ertussen.",
  },
  {
    nr: "02",
    title: "Het lied",
    text: "Een lied is een herinnering aan iets dat je nooit bent vergeten. Ik schrijf niet om te vertellen, maar om te her-inneren wat al klopt.",
  },
  {
    nr: "03",
    title: "De weg",
    text: "Er is geen bestemming. Zoals de dervish draait zonder ergens heen te gaan, zo zing ik — cirkelend om een stil middelpunt.",
  },
  {
    nr: "04",
    title: "Vrijheid",
    text: "Frank en vrij: eerlijk en ongebonden. Niets verkopen, niets bewijzen. Alleen delen wat vanzelf opkwam en mocht blijven.",
  },
];

export const Manifesto = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-50, 50]);

  return (
    <section id="over" ref={ref} data-testid="manifesto-section" className="relative py-28 sm:py-40 overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0 -z-10" aria-hidden="true">
        <img
          src="https://images.pexels.com/photos/36170519/pexels-photo-36170519.jpeg"
          alt=""
          loading="lazy"
          className="w-full h-[130%] object-cover saturate-50 opacity-[0.08]"
        />
      </motion.div>

      <div className="max-w-6xl mx-auto px-6">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="font-sans text-xs font-light tracking-[0.35em] uppercase text-terracotta-500 mb-6"
        >
          03 — Waar dit over gaat
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.1 }}
          className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-sand-900 leading-tight max-w-2xl"
        >
          Vier hoofdstukken, <em className="text-terracotta-500">één stilte</em>
        </motion.h2>

        <div className="mt-20 space-y-20 sm:space-y-28">
          {CHAPTERS.map((ch, i) => (
            <motion.div
              key={ch.nr}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9 }}
              className={`relative max-w-xl ${i % 2 === 1 ? "sm:ml-auto" : ""}`}
            >
              <span
                aria-hidden="true"
                className="absolute -top-14 -left-4 sm:-left-10 font-serif font-light text-8xl sm:text-9xl text-sand-300/60 select-none"
              >
                {ch.nr}
              </span>
              <div className="relative">
                <h3 className="font-serif text-3xl sm:text-4xl font-light tracking-tight text-sand-900">
                  {ch.title}
                </h3>
                <p className="mt-5 font-sans text-base sm:text-lg font-light leading-relaxed text-sand-900/70">
                  {ch.text}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
